import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Textarea,
  Stack,
  Typography,
  Sheet,
} from '@mui/joy';
import { useNavigate } from 'react-router';
import { FC, FormEvent, JSX, useState } from 'react';
import { Input, Loader, PageBreadcrumbs } from '@consolidocs-ui/components';
import {
  FieldErrorType,
  handleApiErrors,
  useToast,
  sortAlphanumeric,
} from '@consolidocs-ui/utils';
import { useGetIndustriesQuery } from '@consolidocs-ui/data-access/IndustryApi';
import { Dropdown } from '@consolidocs-ui/components';
import { IndustryType } from '@consolidocs-ui/types';
import { ErrorType, ContractFormType } from '@consolidocs-ui/types';
import { useCreateContractMutation } from '@consolidocs-ui/data-access/ContractApi';

export const contractInitialState: ContractFormType = {
  name: '',
  code: '',
  description: '',
  industry_types: [],
};

const CreateContract: FC = (): JSX.Element => {
  const [contract, setContract] =
    useState<ContractFormType>(contractInitialState);
  const [errors, setErrors] = useState<FieldErrorType>({});
  const [createContract, { isLoading }] = useCreateContractMutation();
  const { data: industryTypesData, isLoading: isIndustryTypesLoading } =
    useGetIndustriesQuery();
  const navigate = useNavigate();
  const [toastTrigger] = useToast();
  const industryDropdownOptions = sortAlphanumeric(
    industryTypesData?.data?.map((industry: IndustryType) => ({
      label: industry.name,
      value: industry.id,
    })),
    ({ label }) => label
  );

  const handleChange = (field: keyof ContractFormType, value: string) => {
    setContract({ ...contract, [field]: value });
    setErrors({ ...errors, [field]: '' });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const newErrors: FieldErrorType = {};

    if (!contract.name.trim()) {
      newErrors.name = 'Contract name is required';
    }
    if (!contract.code.trim()) {
      newErrors.code = 'Contract code is required';
    }
    if (Object.keys(newErrors).length > 0) {
      return setErrors(newErrors);
    }

    try {
      await createContract(contract).unwrap();
      toastTrigger({
        message: `Contract created successfully`,
        type: 'success',
      });
      setContract(contractInitialState);
      navigate('/contracts');
    } catch (err) {
      console.error('err', err);
      handleApiErrors<ContractFormType>(
        err,
        setErrors,
        (message: string, color: 'success' | 'danger') =>
          toastTrigger({ message, type: color }),
        (err as ErrorType)?.data?.status_message || 'Failed to create contract'
      );
    }
  };

  return (
    <Box sx={{ flex: 1, width: '100%' }}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 9995,
          bgcolor: '#f5f5f5',
          // Cancel the .content wrapper's top padding (p-4) so the bar sticks
          // flush to the top instead of drifting down before it engages.
          mt: { md: -2 },
          pt: { xs: 1, md: 2 },
          pb: 1,
        }}
      >
        <PageBreadcrumbs items={[{ label: 'Create Contract' }]} />
      </Box>

      <Sheet
        variant="outlined"
        sx={{
          width: { xs: '100%', sm: '90%', md: '800px' },
          maxWidth: '800px',
          mx: 'auto',
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 'md',
          boxShadow: 'sm',
          borderColor: 'neutral.outlinedBorder',
          marginTop: { xs: '12px', md: '20px' },
        }}
      >
        <Typography level="h4" mb={2}>
          Create Contract
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ mx: 'auto', mt: 4 }}
        >
          <Stack spacing={2}>
            <FormControl required>
              <FormLabel>Contract Code</FormLabel>
              <Input
                value={contract.code}
                onChange={(e) => handleChange('code', e.target.value)}
                placeholder="e.g., NDA-001"
                required
                error={!!errors['code']}
                errorMessage={errors['code']}
                size="sm"
              />
            </FormControl>

            <FormControl required>
              <FormLabel>Contract Name</FormLabel>
              <Input
                value={contract.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., NDA Agreement"
                required
                error={!!errors['name']}
                errorMessage={errors['name']}
                size="sm"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                minRows={3}
                value={contract.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Optional description"
                size="sm"
              />
            </FormControl>

            <Dropdown
              label="Industry Types"
              options={industryDropdownOptions}
              multiple
              value={contract.industry_types}
              onChange={(_, newValue) =>
                handleChange('industry_types', newValue)
              }
              isLoading={isIndustryTypesLoading}
              error={errors.industry_types}
            />

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                type="button"
                variant="plain"
                color="neutral"
                onClick={() => navigate('/contracts')}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="solid"
                color="primary"
                loading={isLoading}
                loadingIndicator={<Loader size="sm" />}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Sheet>
    </Box>
  );
};

export default CreateContract;
